import { useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";

export const PRIORITIES = {
  alta:  { label: "Alta",  color: "#DC2626", bg: "rgba(220,38,38,0.10)",   border: "rgba(220,38,38,0.35)" },
  media: { label: "Media", color: "#B7955B", bg: "rgba(183,149,91,0.12)",  border: "rgba(183,149,91,0.40)" },
  bassa: { label: "Bassa", color: "#64748B", bg: "rgba(100,116,139,0.10)", border: "rgba(100,116,139,0.30)" },
};

/* Cache modulo: i membri del team cambiano raramente */
let _membersCache = null;
export function useTeamMembers() {
  const [members, setMembers] = useState(_membersCache || []);
  useEffect(() => {
    if (_membersCache) return;
    api.get("/team/assignees")
      .then((r) => { _membersCache = r.data?.items || []; setMembers(_membersCache); })
      .catch(() => {});
  }, []);
  return members;
}

/* Responsabile di un task: assegnatario se presente, altrimenti il creatore */
export function responsible(t) {
  if (t.assigneeId) return { isMe: !!t.assignedToMe, name: t.assignedToMe ? "Tu" : (t.assigneeName || "Collega") };
  return { isMe: t.isMine !== false, name: t.isMine !== false ? "Tu" : (t.ownerName || "Team") };
}

export function initialsOf(name) {
  return (name || "?").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export const PRIORITY_ORDER = ["alta", "media", "bassa"];

export function dueMeta(due, today) {
  if (!due) return null;
  if (due < today) return { kind: "overdue", color: "#DC2626", bg: "rgba(220,38,38,0.10)", label: fmtDueIT(due) };
  if (due === today) return { kind: "today", color: "#8a6e3d", bg: "rgba(183,149,91,0.15)", label: "Oggi" };
  return { kind: "future", color: "#475569", bg: "rgba(100,116,139,0.10)", label: fmtDueIT(due) };
}

function fmtDueIT(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

export function useTodos(clientId) {
  const [items, setItems] = useState([]);
  const [today, setToday] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/todos", { params: clientId ? { clientId } : {} });
      setItems(r.data.items || []);
      if (r.data.today) setToday(r.data.today);
    } catch {} finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const add = useCallback(async ({ title, priority, due, assigneeId }) => {
    const r = await api.post("/todos", { title, priority, due: due || null, assigneeId: assigneeId || null, clientId: clientId || null });
    setItems((prev) => [r.data, ...prev]);
    return r.data;
  }, [clientId]);

  const toggle = useCallback(async (t) => {
    const next = !t.completed;
    // Ottimistico
    setItems((prev) => prev.map((i) => (i.id === t.id ? { ...i, completed: next } : i)));
    try {
      await api.patch(`/todos/${t.id}`, { completed: next });
    } catch {
      setItems((prev) => prev.map((i) => (i.id === t.id ? { ...i, completed: !next } : i)));
    }
  }, []);

  const remove = useCallback(async (t) => {
    setItems((prev) => prev.filter((i) => i.id !== t.id));
    try {
      await api.delete(`/todos/${t.id}`);
    } catch {
      load();
    }
  }, [load]);

  const update = useCallback(async (t, payload) => {
    const r = await api.patch(`/todos/${t.id}`, payload);
    setItems((prev) => prev.map((i) => (i.id === t.id ? r.data : i)));
    return r.data;
  }, []);

  return { items, today, loading, load, add, toggle, remove, update };
}
